import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { useAuth, roleHomePath, type UserRole } from "@/contexts/AuthContext";
import { buildLoginPath } from "@/lib/auth-redirect";

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: UserRole[];
}

export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { isAuthenticated, user, isLoading } = useAuth();
  const location = useLocation();
  const returnTo = location.pathname + location.search;

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Checking your session…
      </div>
    );
  }

  const loginBase =
    allowedRoles?.includes("admin") && !allowedRoles.includes("customer")
      ? "/auth/admin/secure/login"
      : "/auth/login";

  if (!isAuthenticated || !user) {
    return (
      <Navigate
        to={buildLoginPath(loginBase, returnTo)}
        state={{ from: returnTo }}
        replace
      />
    );
  }

  if (allowedRoles && allowedRoles.length > 0) {
    if (!user.role) {
      return (
        <Navigate
          to={buildLoginPath(loginBase, returnTo)}
          state={{ from: returnTo }}
          replace
        />
      );
    }

    if (!allowedRoles.includes(user.role)) {
      return <Navigate to={roleHomePath(user.role)} replace />;
    }
  }

  return <>{children}</>;
}
